<div class="table-wrap" [formGroup]="rootForm">
  <table mat-table [dataSource]="dataSource" class="fpr-table">
    <!-- Read-only columns (auto populated) -->
    <ng-container matColumnDef="productIdentifier">
      <th mat-header-cell *matHeaderCellDef>
        {{ translationService.getProductIdentifierText() }}
      </th>
      <td mat-cell *matCellDef="let r">
        {{ r.productIdentifier }}
        @if (r.isProvisional) {
          <span
            class="external-chip"
            [matTooltip]="translationService.getExternalProductTooltipText()"
            matTooltipPosition="above"
          >
            <mat-icon class="chip-icon">inventory_2</mat-icon>
            <span class="chip-label">{{
              translationService.getExternalProductText()
            }}</span>
          </span>
        }
        @if (rowHasAllZeroQuantities(r)) {
          <span class="no-quantities-label">
            {{ translationService.getNoQuantitiesEnteredText() }}
          </span>
        }
      </td>
    </ng-container>

    <ng-container matColumnDef="sku" sticky>
      <th mat-header-cell *matHeaderCellDef>
        {{ translationService.getSkuText() }}
      </th>
      <td mat-cell *matCellDef="let r">{{ r.sku }}</td>
    </ng-container>

    <ng-container matColumnDef="productName" sticky>
      <th mat-header-cell *matHeaderCellDef class="frozen-column-cell">
        {{ translationService.getProductNameText() }}
      </th>
      <td mat-cell *matCellDef="let r" class="frozen-column-cell">
        {{ r.productName }}
      </td>
    </ng-container>

    <ng-container matColumnDef="supplier">
      <th mat-header-cell *matHeaderCellDef>
        {{ translationService.getSupplierText() }}
      </th>
      <td mat-cell *matCellDef="let r">
        {{ r.supplier ?? translationService.getNaText() }}
      </td>
    </ng-container>

    <ng-container matColumnDef="category">
      <th mat-header-cell *matHeaderCellDef>
        {{ translationService.getCategoryText() }}
      </th>
      <td mat-cell *matCellDef="let r">{{ r.category }}</td>
    </ng-container>

    <ng-container matColumnDef="subCategory">
      <th mat-header-cell *matHeaderCellDef>
        {{ translationService.getSubCategoryText() }}
      </th>
      <td mat-cell *matCellDef="let r">{{ r.subCategory }}</td>
    </ng-container>

    <ng-container matColumnDef="resins">
      <th mat-header-cell *matHeaderCellDef>
        {{ translationService.getResinBreakdownText() }}
      </th>
      <td mat-cell *matCellDef="let r">
        @if (!r.isTotalRow) {
          <button
            mat-stroked-button
            class="resin-chip"
            color="primary"
            [disabled]="!r.plasticResins?.length"
            (click)="openResinBreakdown(r)"
          >
            <mat-icon class="icon">polymer</mat-icon>
            <span class="label"
              >{{ translationService.getResinsText() }} ({{
                r.plasticResins?.length || 0
              }})</span
            >
          </button>
        }
      </td>
    </ng-container>

    <ng-container matColumnDef="reportingDate">
      <th mat-header-cell *matHeaderCellDef>
        {{ translationService.getReportingDateText() }}
      </th>
      <td mat-cell *matCellDef="let r">
        {{
          r.reportingDate
            ? (r.reportingDate | date: 'MMMM d, y')
            : translationService.getMultipleText()
        }}
      </td>
    </ng-container>

    <!-- Editable columns (user entered) -->
    <ng-container matColumnDef="methodUsed">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getMethodUsedText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="sm">
          <mat-select
            formControlName="methodUsed"
            [disableOptionCentering]="true"
            placeholder="—"
          >
            @for (opt of getMethodOptions(r); track opt) {
              <mat-option [value]="opt">
                {{ opt || '—' }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyManufactured">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyManufacturedInCanadaText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyManufactured"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyImported">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyImportedText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyImported"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyPlacedOnMarket">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyPlacedOnMarketText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyPlacedOnMarket"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyAlberta">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyAlbertaText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyAlberta"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyBritishColumbia">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyBritishColumbiaText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyBritishColumbia"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyManitoba">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyManitobaText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyManitoba"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyNewBrunswick">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyNewBrunswickText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyNewBrunswick"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyNewfoundlandAndLabrador">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyNewfoundlandAndLabradorText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyNewfoundlandAndLabrador"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyNovaScotia">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyNovaScotiaText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyNovaScotia"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyOntario">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyOntarioText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyOntario"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyPrinceEdwardIsland">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyPrinceEdwardIslandText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyPrinceEdwardIsland"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyQuebec">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyQuebecText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyQuebec"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtySaskatchewan">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtySaskatchewanText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtySaskatchewan"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyNorthwestTerritories">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyNorthwestTerritoriesText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyNorthwestTerritories"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyNunavut">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyNunavutText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyNunavut"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyYukon">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getQtyYukonText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyYukon"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <!-- Waste generation and end-of-life columns -->
    <ng-container matColumnDef="qtyWasteGeneratedAtPremises">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getWasteGeneratedAtPremisesText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyWasteGeneratedAtPremises"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyCollectedAtEndOfLife">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getCollectedAtEndOfLifeText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyCollectedAtEndOfLife"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedDirectReuse">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getDivertedDirectReuseText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedDirectReuse"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedRefurbishment">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getDivertedRefurbishmentText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedRefurbishment"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedRepair">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getDivertedRepairText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedRepair"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedRemanufacturing">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getDivertedRemanufacturingText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedRemanufacturing"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedMechanicalRecycling">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getDivertedMechanicalRecyclingText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedMechanicalRecycling"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedChemicalRecycling">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getDivertedChemicalRecyclingText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedChemicalRecycling"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedProcessingIntoChemicalsFuels">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getDivertedProcessingIntoChemicalsFuelsText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedProcessingIntoChemicalsFuels"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedComposting">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getDivertedCompostingText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedComposting"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedIncinerationWithEnergyRecovery">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getDivertedIncinerationWithEnergyRecoveryText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedIncinerationWithEnergyRecovery"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyDivertedIncinerationIndustrialProcesses">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{
          translationService.getDivertedIncinerationIndustrialProcessesText()
        }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyDivertedIncinerationIndustrialProcesses"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyFinalDisposalIncinerationNoEnergy">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{
          translationService.getFinalDisposalIncinerationNoEnergyRecoveryText()
        }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyFinalDisposalIncinerationNoEnergy"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <ng-container matColumnDef="qtyFinalDisposalLandfill">
      <th mat-header-cell *matHeaderCellDef class="editable">
        {{ translationService.getFinalDisposalLandfillText() }}
      </th>
      <td mat-cell *matCellDef="let r; let i = index" [formGroup]="form.at(i)">
        <mat-form-field appearance="outline" class="xs">
          <input
            matInput
            type="number"
            formControlName="qtyFinalDisposalLandfill"
            min="0"
            step="1"
            inputmode="numeric"
          />
        </mat-form-field>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
    <tr
      mat-row
      *matRowDef="let row; let i = index; columns: displayedColumns"
      [class.total-row]="row.isTotalRow"
      [class.group-child]="isGroupChild(i)"
      [class.group-child-start]="isGroupChildStart(i)"
      [class.group-child-end]="isGroupChildEnd(i)"
      [class.external-row]="row.isProvisional"
    ></tr>

    @if (!dataSource || dataSource.data.length === 0) {
      <tr class="empty">
        <td [attr.colspan]="displayedColumns.length">
          <div class="empty-cta">
            <span>{{ translationService.getNoRowsText() }}</span>
          </div>
        </td>
      </tr>
    }
  </table>
</div>
